import { useEffect, useState } from "react";
import { Link2, Loader2, Copy, ExternalLink, Ban, Clock } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Copenhagen" }) : "");

// Sikkert, opgaveafgrænset kundelink med udløb og tilbagekaldelse. Kan genåbnes i gyldighedsperioden.
export default function PortalLinkCard({ jobId }) {
    const api = useEmployeeApi();
    const [links, setLinks] = useState(null);
    const [busy, setBusy] = useState("");

    useEffect(() => { api.getPortalLinks(jobId).then((d) => setLinks(d.links || [])).catch(() => setLinks([])); }, [api, jobId]);

    const create = async () => {
        setBusy("create");
        try { const r = await api.createPortalLink(jobId, 72); setLinks((l) => [r.link, ...(l || [])]); toast.success("Kundelink oprettet (gyldigt 72 timer)"); }
        catch { toast.error("Kunne ikke oprette link"); }
        finally { setBusy(""); }
    };
    const revoke = async (l) => {
        setBusy(l.id);
        try { const r = await api.revokePortalLink(jobId, l.id); setLinks((ls) => ls.map((x) => (x.id === l.id ? r.link : x))); toast.success("Link tilbagekaldt"); }
        catch { toast.error("Kunne ikke tilbagekalde"); }
        finally { setBusy(""); }
    };
    const copy = async (l) => {
        const url = `${window.location.origin}${l.url_path}`;
        try { await navigator.clipboard.writeText(url); toast.success("Link kopieret"); } catch { toast(url); }
    };

    const active = (links || []).filter((l) => l.state === "active");
    const inactive = (links || []).filter((l) => l.state !== "active");

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="portal-link-card">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" />Kundeportal</h2>
            {links === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-300" /> : (
                <>
                    {active.length === 0 && <p className="text-sm text-slate-500 mb-3">Kunden kan følge status, ankomst og eksplicit delte noter via et sikkert link.</p>}
                    <ul className="space-y-2 mb-3">
                        {active.map((l) => (
                            <li key={l.id} className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm" data-testid="portal-link-active">
                                <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" />Gyldigt til {fmt(l.expires_at)} · åbnet {l.opens}×</p>
                                <div className="grid grid-cols-3 gap-1.5 mt-2">
                                    <button onClick={() => copy(l)} data-testid="portal-link-copy" className="h-9 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 flex items-center justify-center gap-1"><Copy className="w-3.5 h-3.5" />Kopiér</button>
                                    <a href={l.url_path} target="_blank" rel="noreferrer" data-testid="portal-link-open" className="h-9 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 flex items-center justify-center gap-1"><ExternalLink className="w-3.5 h-3.5" />Åbn</a>
                                    <button onClick={() => revoke(l)} disabled={busy === l.id} data-testid="portal-link-revoke" className="h-9 rounded-lg bg-white border border-red-200 text-xs font-semibold text-red-600 flex items-center justify-center gap-1">{busy === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}Tilbagekald</button>
                                </div>
                            </li>
                        ))}
                        {inactive.slice(0, 2).map((l) => (
                            <li key={l.id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-400" data-testid="portal-link-inactive">{l.state === "revoked" ? "Tilbagekaldt" : "Udløbet"} · oprettet {fmt(l.created_at)}</li>
                        ))}
                    </ul>
                    <button onClick={create} disabled={busy === "create"} data-testid="portal-link-create"
                        className="w-full h-10 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Opret kundelink (72 timer)
                    </button>
                </>
            )}
        </section>
    );
}
